import {
  useCreateAssessmentMutation,
  useGetSingleAssessmentQuery,
  useUpdateAssessmentMutation,
} from '@/app/features/recruitment/assessments/assessment.api';
import CustomDrawer from '@/components/CustomDrawer';
import { cn } from "@/lib/cn";
import type { CustomModalProps } from '@/components/CustomModal';
import WithLoader from '@/components/WithLoader';
import { CirclePlusIcon } from '@/icons';
import { getErrorMessage } from '@/lib/error';
import { toast } from '@/lib/toast';
import { Button, Form, Input, InputNumber } from 'antd';
import TextArea from 'antd/es/input/TextArea';
import { useEffect } from 'react';
import Question from './Question';

type Props = CustomModalProps & { id?: string; type?: string };

export default function ManageAssessment({
  handleClose,
  id,
  open,
  type,
}: Props) {
  const [form] = Form.useForm();

  const {
    isLoading,
    isFetching,
    data: assessmentRes,
  } = useGetSingleAssessmentQuery(id as string, { skip: !id });

  const [createAssessment, { isLoading: isCreating }] =
    useCreateAssessmentMutation();
  const [updateAssessment, { isLoading: isUpdating }] =
    useUpdateAssessmentMutation();

  useEffect(() => {
    if (assessmentRes?.success && assessmentRes?.data) {
      const data = assessmentRes.data;
      form.setFieldsValue({
        title: data.title,
        status: data.status,
        instruction: data.instruction,
        passing_mark: Number(data.passing_mark),
        embedded_resources: data.embedded_resources,
        questions: data?.questions?.map((q: any) => ({
          title: q.title,
          question_type: q.question_type,
          mark: Number(q.mark),
          options: q?.options?.map((opt: any) => ({
            value: opt.value,
            is_correct: opt.is_correct,
          })),
        })),
      });
    } else {
      form.resetFields();
    }
  }, [assessmentRes, form, open]);

  const handleSubmit = async (values: any) => {
    const {
      title,
      status,
      questions,
      instruction,
      passing_mark,
      embedded_resources,
    } = values || {};
    const payload = {
      title,
      status,
      instruction,
      embedded_resources,
      passing_mark: Number(passing_mark),
      questions: questions?.map((q: any) => ({
        ...q,
        mark: Number(q.mark),
      })),
    };

    try {
      let res: any;
      if (id) {
        res = await updateAssessment({ id, body: payload }).unwrap();
      } else {
        res = await createAssessment(payload).unwrap();
      }

      if (res.success) {
        toast('success', getErrorMessage(res, 'Assessment saved successfully'));
        handleClose();
      } else {
        toast('error', getErrorMessage(res, 'Failed to save assessment'));
      }
    } catch (error) {
      toast('error', getErrorMessage(error, 'Failed to save assessment'));
    }
  };

  return (
    <CustomDrawer
      open={open}
      width={850}
      onClose={() => {
        handleClose();
        form.resetFields();
      }}
      title={
        id && type === 'VIEW'
          ? 'View Assessment'
          : id
            ? 'Update Assessment'
            : 'Create New Assessment'
      }
    >
      <WithLoader isLoading={isLoading || isFetching}
        className={cn(
          "rounded-lg",
          (isLoading || isFetching) && "h-[calc(100vh_-_105px)]",
        )}>
        <Form
          form={form}
          size="large"
          disabled={type === 'VIEW'}
          layout="vertical"
          className="custom-form space-y-6"
          onFinish={handleSubmit}
        >
          <Form.Item name="status" initialValue="active" noStyle hidden />

          <div className="grid grid-cols-2 gap-6">
            <Form.Item
              label="Title"
              name="title"
              rules={[
                {
                  required: true,
                  message: 'Please enter title',
                },
              ]}
            >
              <Input />
            </Form.Item>

            <Form.Item
              label="Passing Marks"
              name="passing_mark"
              rules={[
                { required: true, message: 'Please enter passing marks' },
                {
                  min: 1,
                  type: 'number',
                  message: 'Passing mark should not be less then 1',
                },
              ]}
            >
              <InputNumber placeholder="0" controls={false} />
            </Form.Item>
          </div>

          <Form.Item
            label="Embedded Resources ( Video/Audio/Document) "
            name="embedded_resources"
          >
            <Input />
          </Form.Item>

          <Form.Item
            label="Instruction"
            name="instruction"
            rules={[
              {
                required: true,
                message: 'Please enter instruction',
              },
            ]}
          >
            <TextArea rows={6} />
          </Form.Item>

          <div>
            <h3 className="text-xl font-medium w-full border-b border-white pb-2 mb-8">
              Questions :
            </h3>

            <Form.List name="questions" initialValue={[{}]}>
              {(fields, { add, remove }) => (
                <>
                  <div className="space-y-4">
                    {fields.map((field) => (
                      <Question key={field.key} field={field} remove={remove} />
                    ))}
                  </div>
                  {type !== 'VIEW' && (
                    <button
                      type="button"
                      onClick={() => add({})}
                      className="w-full flex justify-center text-theme-primary relative mt-6"
                    >
                      <div className="absolute left-0 right-0 w-full border-b border-theme-primary border-dashed bottom-2" />
                      <div className="bg-theme-slate-900 relative z-10 px-2">
                        <CirclePlusIcon size={20} />
                      </div>
                    </button>
                  )}
                </>
              )}
            </Form.List>
          </div>

          {type !== 'VIEW' && (
            <div className="flex justify-end gap-4">
              {/* <Button
                size="large"
                htmlType='submit'
                disabled={isCreating || isUpdating}
                onClick={() => form.setFieldValue('status', 'draft')}
                className="btn-primary-bordered !min-w-28"
              >
                Draft
              </Button> */}

              <Button
                htmlType="submit"
                className="btn-primary !min-w-28"
                loading={isCreating || isUpdating}
                onClick={() => form.setFieldValue('status', 'active')}
              >
                Save
              </Button>
            </div>
          )}
        </Form>
      </WithLoader>
    </CustomDrawer>
  );
}
